/**
 * Games Management Page
 * View game history and statistics with advanced filtering
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface Game {
  id: string;
  tableId: string;
  tableName: string;
  handNumber: number;
  phase: string;
  pot: number;
  winnerId?: string;
  winnerUsername?: string;
  winningHand?: string;
  playerCount: number;
  duration?: number; // in seconds
  startTime: string;
  endTime?: string;
  isCompleted: boolean;
}

interface GameDetails extends Game {
  players: Array<{
    id: string;
    username: string;
    seatPosition: number;
    chipCount: number;
    holeCards?: string[];
    finalHand?: string;
    amountWon?: number;
  }>;
  communityCards: string[];
  handHistory: Array<{
    round: string;
    actions: Array<{
      playerId: string;
      playerUsername: string;
      action: string;
      amount?: number;
      timestamp: string;
    }>;
  }>;
}

interface Statistics {
  totalGames: number;
  todayGames: number;
  avgPot: number;
  largestPot: number;
  avgDuration: number; // in seconds
  mostActivePlayer?: {
    username: string;
    gamesPlayed: number;
  };
  totalHandsPlayed: number;
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  playerUsername: string;
  minPot: string;
  maxPot: string;
  tableName: string;
  status: 'all' | 'completed' | 'active';
}

const GamesPage: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Statistics | null>(null);

  // Modals
  const [detailsModalGame, setDetailsModalGame] = useState<GameDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    dateFrom: '',
    dateTo: '',
    playerUsername: '',
    minPot: '',
    maxPot: '',
    tableName: '',
    status: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadGames();
    loadStats();
  }, [page, filters]);

  const loadGames = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params: any = { page, limit: 100 };

      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.playerUsername) params.playerUsername = filters.playerUsername;
      if (filters.minPot) params.minPot = parseInt(filters.minPot);
      if (filters.maxPot) params.maxPot = parseInt(filters.maxPot);
      if (filters.tableName) params.tableName = filters.tableName;
      if (filters.status !== 'all') params.isCompleted = filters.status === 'completed';

      const data = await adminService.getGames(params);
      setGames(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Failed to load games:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminService.getGameStatistics();
      setStats(data);
    } catch (error) {
      console.error('Failed to load game stats:', error);
    }
  };

  const viewGameDetails = async (gameId: string) => {
    try {
      setLoadingDetails(true);
      const data = await adminService.getGameDetails(gameId);
      setDetailsModalGame(data);
    } catch (error) {
      console.error('Failed to load game details:', error);
      alert('Failed to load game details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      playerUsername: '',
      minPot: '',
      maxPot: '',
      tableName: '',
      status: 'all',
    });
    setPage(1);
  };

  const exportToCSV = () => {
    const headers = ['Game ID', 'Table Name', 'Hand #', 'Players', 'Winner', 'Pot Size', 'Duration (s)', 'Status', 'Started', 'Ended'];
    const rows = games.map(game => [
      game.id,
      game.tableName,
      game.handNumber,
      game.playerCount,
      game.winnerUsername || 'N/A',
      game.pot,
      game.duration || 'N/A',
      game.isCompleted ? 'Completed' : 'Active',
      new Date(game.startTime).toISOString(),
      game.endTime ? new Date(game.endTime).toISOString() : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `games-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDetailsToJSON = (gameDetails: GameDetails) => {
    const json = JSON.stringify(gameDetails, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `game-${gameDetails.id}-history.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePDFReport = () => {
    // Placeholder for PDF generation
    alert('PDF report generation coming soon! This would generate a comprehensive report with charts and statistics.');
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Games & History</h1>
            <p className="text-purple-100 mt-1">Complete hand history and analytics</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={exportToCSV}
              disabled={games.length === 0}
              className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>📊</span>
              <span>Export CSV</span>
            </button>
            <button
              onClick={generatePDFReport}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-400 text-white rounded-lg transition-colors font-medium flex items-center space-x-2"
            >
              <span>📄</span>
              <span>Generate Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Panel */}
      {stats && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📈 Platform Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Total Hands Played */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="text-sm text-blue-600 font-semibold mb-1">Total Hands Played</div>
              <div className="text-3xl font-bold text-blue-900">
                {stats.totalHandsPlayed?.toLocaleString() || stats.totalGames?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-blue-600 mt-1">All time</div>
            </div>

            {/* Biggest Pot */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="text-sm text-green-600 font-semibold mb-1">Biggest Pot</div>
              <div className="text-3xl font-bold text-green-900">
                {stats.largestPot?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-green-600 mt-1">chips</div>
            </div>

            {/* Most Active Player */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="text-sm text-purple-600 font-semibold mb-1">Most Active Player</div>
              <div className="text-xl font-bold text-purple-900 truncate">
                {stats.mostActivePlayer?.username || 'N/A'}
              </div>
              <div className="text-xs text-purple-600 mt-1">
                {stats.mostActivePlayer?.gamesPlayed || 0} games
              </div>
            </div>

            {/* Average Pot Size */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <div className="text-sm text-orange-600 font-semibold mb-1">Average Pot Size</div>
              <div className="text-3xl font-bold text-orange-900">
                {Math.round(stats.avgPot || 0).toLocaleString()}
              </div>
              <div className="text-xs text-orange-600 mt-1">chips</div>
            </div>

            {/* Average Duration */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200">
              <div className="text-sm text-pink-600 font-semibold mb-1">Average Duration</div>
              <div className="text-2xl font-bold text-pink-900">
                {formatDuration(stats.avgDuration)}
              </div>
              <div className="text-xs text-pink-600 mt-1">per hand</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-gray-700 font-medium hover:text-gray-900 flex items-center space-x-2"
          >
            <span>{showFilters ? '▼' : '▶'}</span>
            <span>Advanced Filters</span>
          </button>
          {showFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Player Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Player Username
              </label>
              <input
                type="text"
                placeholder="Search by player..."
                value={filters.playerUsername}
                onChange={(e) => handleFilterChange('playerUsername', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Table Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Table Name
              </label>
              <input
                type="text"
                placeholder="Search table..."
                value={filters.tableName}
                onChange={(e) => handleFilterChange('tableName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Min Pot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Pot Size
              </label>
              <input
                type="number"
                placeholder="0"
                value={filters.minPot}
                onChange={(e) => handleFilterChange('minPot', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Max Pot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Pot Size
              </label>
              <input
                type="number"
                placeholder="999999"
                value={filters.maxPot}
                onChange={(e) => handleFilterChange('maxPot', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Game Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Games</option>
                <option value="completed">Completed Only</option>
                <option value="active">Active Only</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Games Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {games.length} of {total.toLocaleString()} games
            </div>
            <div className="text-sm font-medium text-gray-700">
              Page {page} of {totalPages}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <div className="text-gray-500 mt-4">Loading games...</div>
          </div>
        ) : games.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-4xl mb-3">🎲</div>
            <div className="text-lg font-medium">No games found</div>
            <div className="text-sm mt-1">Try adjusting your filters</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Game ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Table Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Players
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Winner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pot Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {games.map((game) => (
                    <tr key={game.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">
                        {game.id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {game.tableName || `Table ${game.tableId.slice(0, 6)}`}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="font-bold text-blue-600">{game.playerCount}</span> players
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {game.winnerUsername ? (
                          <div>
                            <div className="font-medium text-green-700">{game.winnerUsername}</div>
                            {game.winningHand && (
                              <div className="text-xs text-gray-500">{game.winningHand}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">In Progress</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600">
                        {game.pot.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDuration(game.duration)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            game.isCompleted
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {game.isCompleted ? 'Completed' : game.phase}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(game.startTime).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => viewGameDetails(game.id)}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded transition-colors"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {page} of {totalPages} ({total.toLocaleString()} total games)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <div className="px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm font-bold text-purple-700">
                  {page}
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Game Details Modal */}
      {detailsModalGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Hand History</h2>
                <p className="text-purple-100 text-sm mt-1">
                  Game {detailsModalGame.id.slice(0, 12)}... • Hand #{detailsModalGame.handNumber}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => exportDetailsToJSON(detailsModalGame)}
                  className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium text-sm"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => setDetailsModalGame(null)}
                  className="text-white hover:text-purple-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-6">
              {loadingDetails ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  <div className="text-gray-500 mt-4">Loading hand history...</div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Game Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="text-sm text-purple-600 font-semibold">Table</div>
                      <div className="text-lg font-bold text-purple-900 mt-1">
                        {detailsModalGame.tableName}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="text-sm text-green-600 font-semibold">Total Pot</div>
                      <div className="text-2xl font-bold text-green-900 mt-1">
                        {detailsModalGame.pot.toLocaleString()} chips
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-sm text-blue-600 font-semibold">Duration</div>
                      <div className="text-lg font-bold text-blue-900 mt-1">
                        {formatDuration(detailsModalGame.duration)}
                      </div>
                    </div>
                  </div>

                  {/* Community Cards */}
                  {detailsModalGame.communityCards && detailsModalGame.communityCards.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Community Cards</h3>
                      <div className="flex space-x-2">
                        {detailsModalGame.communityCards.map((card, idx) => (
                          <div
                            key={idx}
                            className="bg-white border-2 border-gray-300 rounded-lg px-4 py-3 text-center font-bold text-lg shadow"
                          >
                            {card}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Players */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Players at Table</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {detailsModalGame.players.map((player) => (
                        <div
                          key={player.id}
                          className={`rounded-lg border-2 p-4 ${
                            player.amountWon && player.amountWon > 0
                              ? 'bg-green-50 border-green-300'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                                {player.seatPosition + 1}
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">{player.username}</div>
                                <div className="text-sm text-gray-500">Seat {player.seatPosition + 1}</div>
                              </div>
                            </div>
                            {player.amountWon && player.amountWon > 0 && (
                              <div className="text-green-600 font-bold text-lg">
                                🏆 +{player.amountWon.toLocaleString()}
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                              <div className="text-xs text-gray-500">Chips</div>
                              <div className="font-semibold text-gray-800">
                                {player.chipCount.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Final Hand</div>
                              <div className="font-semibold text-gray-800 text-sm">
                                {player.finalHand || 'Folded'}
                              </div>
                            </div>
                          </div>

                          {player.holeCards && player.holeCards.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs text-gray-500 mb-2">Hole Cards</div>
                              <div className="flex space-x-2">
                                {player.holeCards.map((card, idx) => (
                                  <div
                                    key={idx}
                                    className="bg-white border border-gray-300 rounded px-2 py-1 font-bold text-sm"
                                  >
                                    {card}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hand History Timeline */}
                  {detailsModalGame.handHistory && detailsModalGame.handHistory.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 mb-3">Betting Timeline</h3>
                      <div className="space-y-4">
                        {detailsModalGame.handHistory.map((round, roundIdx) => (
                          <div key={roundIdx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="font-bold text-purple-700 mb-3 uppercase text-sm">
                              {round.round}
                            </div>
                            <div className="space-y-2">
                              {round.actions.map((action, actionIdx) => (
                                <div
                                  key={actionIdx}
                                  className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border border-gray-200"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                                      {action.playerUsername.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-900">
                                        {action.playerUsername}
                                      </span>
                                      <span className="text-gray-500 mx-2">•</span>
                                      <span
                                        className={`font-semibold ${
                                          action.action === 'FOLD'
                                            ? 'text-red-600'
                                            : action.action === 'RAISE' || action.action === 'BET'
                                            ? 'text-green-600'
                                            : 'text-blue-600'
                                        }`}
                                      >
                                        {action.action}
                                      </span>
                                      {action.amount && action.amount > 0 && (
                                        <span className="ml-2 font-bold text-gray-900">
                                          {action.amount.toLocaleString()} chips
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(action.timestamp).toLocaleTimeString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winner Summary */}
                  {detailsModalGame.winnerUsername && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🏆</div>
                        <div className="text-lg text-green-700 font-semibold">Winner</div>
                        <div className="text-2xl font-bold text-green-900 mt-1">
                          {detailsModalGame.winnerUsername}
                        </div>
                        {detailsModalGame.winningHand && (
                          <div className="text-green-700 mt-2">
                            with <span className="font-bold">{detailsModalGame.winningHand}</span>
                          </div>
                        )}
                        <div className="text-3xl font-bold text-green-600 mt-3">
                          {detailsModalGame.pot.toLocaleString()} chips
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setDetailsModalGame(null)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesPage;
